/**
 * Status Chip Component
 * Reusable status indicator with consistent styling
 */

import React from "react";
import { Chip, ChipProps } from "@mui/material";
import { alpha, darken } from "@mui/material/styles";
import {
  CheckCircle as ActiveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Schedule as PendingIcon,
  Block as BlockedIcon,
  School as GraduatedIcon,
  HourglassEmpty as WaitingIcon,
  Done as CompletedIcon,
  Error as ErrorIcon,
  Undo as RefundIcon,
  ReportProblem as DisputedIcon,
} from "@mui/icons-material";

export type StatusType =
  | "active"
  | "inactive"
  | "pending"
  | "approved"
  | "rejected"
  | "withdrawn"
  | "graduated"
  | "archived"
  | "provisional"
  | "completed"
  | "cancelled"
  | "waiting"
  | "error"
  | "open"
  | "closed"
  // Payment statuses
  | "succeeded"
  | "failed"
  | "refunded"
  | "partially_refunded"
  | "disputed"
  | "requires_payment_method"
  | "requires_confirmation"
  | "requires_action"
  | "processing"
  | "requires_capture"
  // Invoice statuses
  | "payment_processing";

export interface StatusConfig {
  label: string;
  color: ChipProps["color"];
  icon?: React.ReactElement;
}

const statusConfigs: Record<StatusType, StatusConfig> = {
  active: {
    label: "Active",
    color: "success",
    icon: <ActiveIcon />,
  },
  inactive: {
    label: "Inactive",
    color: "default",
    icon: <BlockedIcon />,
  },
  pending: {
    label: "Pending",
    color: "warning",
    icon: <PendingIcon />,
  },
  approved: {
    label: "Approved",
    color: "success",
    icon: <ActiveIcon />,
  },
  rejected: {
    label: "Rejected",
    color: "error",
    icon: <CancelIcon />,
  },
  withdrawn: {
    label: "Withdrawn",
    color: "warning",
    icon: <CancelIcon />,
  },
  graduated: {
    label: "Graduated",
    color: "info",
    icon: <GraduatedIcon />,
  },
  archived: {
    label: "Archived",
    color: "default",
    icon: <BlockedIcon />,
  },
  provisional: {
    label: "Provisional",
    color: "warning",
    icon: <WarningIcon />,
  },
  completed: {
    label: "Completed",
    color: "success",
    icon: <CompletedIcon />,
  },
  cancelled: {
    label: "Cancelled",
    color: "error",
    icon: <CancelIcon />,
  },
  waiting: {
    label: "Waiting",
    color: "info",
    icon: <WaitingIcon />,
  },
  error: {
    label: "Error",
    color: "error",
    icon: <ErrorIcon />,
  },
  open: {
    label: "Open",
    color: "success",
    icon: <ActiveIcon />,
  },
  closed: {
    label: "Closed",
    color: "warning",
    icon: <BlockedIcon />,
  },
  // Payment statuses
  succeeded: {
    label: "Succeeded",
    color: "success",
    icon: <CompletedIcon />,
  },
  failed: {
    label: "Failed",
    color: "error",
    icon: <CancelIcon />,
  },
  refunded: {
    label: "Refunded",
    color: "info",
    icon: <RefundIcon />,
  },
  partially_refunded: {
    label: "Partially Refunded",
    color: "info",
    icon: <RefundIcon />,
  },
  disputed: {
    label: "Disputed",
    color: "warning",
    icon: <DisputedIcon />,
  },
  requires_payment_method: {
    label: "Requires Payment",
    color: "warning",
    icon: <PendingIcon />,
  },
  requires_confirmation: {
    label: "Requires Confirmation",
    color: "warning",
    icon: <PendingIcon />,
  },
  requires_action: {
    label: "Requires Action",
    color: "warning",
    icon: <WarningIcon />,
  },
  processing: {
    label: "Processing",
    color: "info",
    icon: <WaitingIcon />,
  },
  payment_processing: {
    label: "Payment Processing",
    color: "info",
    icon: <WaitingIcon />,
  },
  requires_capture: {
    label: "Requires Capture",
    color: "warning",
    icon: <PendingIcon />,
  },
};

/**
 * Resolve the canonical config (label + MUI color + icon) for a status.
 * Falls back to a title-cased label with the default color for unknown values.
 * Use this (or `getStatusColor`) instead of re-implementing per-page status maps.
 */
export function getStatusConfig(status: StatusType | string): StatusConfig {
  return (
    statusConfigs[status as StatusType] || {
      label: status.charAt(0).toUpperCase() + status.slice(1),
      color: "default" as const,
    }
  );
}

/**
 * Canonical MUI color for a status string — the single source of truth that
 * replaces the inline `getStatusColor` helpers scattered across pages.
 */
export function getStatusColor(status: StatusType | string): ChipProps["color"] {
  return getStatusConfig(status).color;
}

export interface StatusChipProps {
  /** Status type */
  status: StatusType | string;
  /** Custom label override */
  label?: string;
  /** Chip size */
  size?: "small" | "medium";
  /** Chip variant */
  variant?: "filled" | "outlined";
  /** Show icon */
  showIcon?: boolean;
  /** Custom color override */
  color?: ChipProps["color"];
  /** Custom styles */
  sx?: ChipProps["sx"];
}

// Palette keys that carry main/dark tints (everything except "default").
type TintKey = "primary" | "secondary" | "error" | "info" | "success" | "warning";

const StatusChipComponent: React.FC<StatusChipProps> = ({
  status,
  label,
  size = "small",
  variant = "filled",
  showIcon = true,
  color,
  sx,
}) => {
  const config = getStatusConfig(status);
  const chipColor = color || config.color;

  return (
    <Chip
      label={label || config.label}
      size={size}
      variant={variant}
      icon={showIcon ? config.icon : undefined}
      // Soft-pill treatment: a subtle real-color tint with dark, AA-legible
      // text and no heavy border/solid fill — reads as a calm status tag
      // rather than a loud outlined chip. "default" falls back to a neutral
      // grey pill. Overrides both filled + outlined variants.
      sx={[
        (theme) => {
          const isDefault = !chipColor || chipColor === "default";
          const entry = isDefault ? undefined : theme.palette[chipColor as TintKey];
          const bgBase = entry ? entry.main : theme.palette.text.primary;
          // Text must stay legible on the tint in BOTH modes AND meet WCAG-AA
          // (4.5:1). On light mode the .dark shade alone failed for warning
          // (#e65100 = 3.08) and error (4.33), so darken it further; the dark
          // mode uses the lighter .main on the dark-mode tint.
          const fg = entry
            ? theme.palette.mode === "light"
              ? darken(entry.dark, 0.22)
              : entry.main
            : theme.palette.text.secondary;
          return {
            border: "none",
            fontWeight: 600,
            bgcolor: alpha(bgBase, isDefault ? 0.09 : 0.14),
            color: fg,
            "& .MuiChip-icon": { color: "inherit", fontSize: 16 },
          };
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};

/**
 * Memoized StatusChip - prevents unnecessary re-renders in lists
 */
export const StatusChip = React.memo(StatusChipComponent);

export default StatusChip;
